import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  Circle,
  ArrowLeft,
  Key,
  Power,
  Droplet,
  AlertTriangle,
  Trash2,
  Home,
  ChevronRight,
  Car,
  Zap,
  Flame,
  Thermometer,
  Lightbulb,
} from "lucide-react";

// Check-in Images
import Parking0 from "../../images/check-in/1_parking/1_parking_0.webp";
import Parking1 from "../../images/check-in/1_parking/1_parking_1.webp";
import Parking2 from "../../images/check-in/1_parking/1_parking_2.webp";
import Parking3 from "../../images/check-in/1_parking/1_parking_3.webp";

import Strom1 from "../../images/check-in/2_strom/strom_1.webp";
import Strom2 from "../../images/check-in/2_strom/strom_2.webp";
import Strom3Aus from "../../images/check-in/2_strom/strom_3_aus.webp";
import Strom3Ein from "../../images/check-in/2_strom/strom_3_ein.webp";

import Wasser0 from "../../images/check-in/3_wasser/wasser_0.webp";
import Wasser1 from "../../images/check-in/3_wasser/wasser_1.webp";
import WasserAuf from "../../images/check-in/3_wasser/wasser_auf.webp";
import WasserZu from "../../images/check-in/3_wasser/wasser_zu.webp";

import Gas2Aus from "../../images/check-in/4_gas/gas_2_aus.webp";
import Gas2Zu from "../../images/check-in/4_gas/gas_2_zu.webp";

import Klima1 from "../../images/check-in/5_klima/zimmer1_klima_ein_aus.webp";
import Klima2 from "../../images/check-in/5_klima/zimmer2_klima_ein_aus.webp";

import KuecheBeleuchtung from "../../images/check-in/6_kaffee_und küche/beleuchtung_kueche.webp";
import JunkerApp from "../../images/check-in/7_müll/junker-app.webp";
import FridgeOff from "../../images/check-out/Kühlschrank_ausschalten.webp";
import Fenster1 from "../../images/check-out/Fenster_oben1.webp";
import Fenster2 from "../../images/check-out/Fenster_oben2.webp";
import GasAbdrehen from "../../images/check-out/Gas_abdrehen.webp";
import GeschierspuelerKochfeld from "../../images/check-out/Geschierspueler_Kochfeld.webp";
import Kaffemaschine from "../../images/check-out/Kaffemaschine.webp";

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  longDescription?: React.ReactNode;
  icon: React.ReactNode;
  photos?: string[]; // Assuming photo URLs
}

interface ChecklistProps {
  type?: "checkin" | "checkout";
}

const checkInItems: ChecklistItem[] = [
  {
    id: "parking",
    title: "Parking",
    description: "Hier kannst du parken.",
    longDescription: "Parke dein Auto bitte auf dem grün markierten Parkplatz.",
    icon: <Car className="text-primary" size={24} />,
    photos: [Parking0, Parking1, Parking2, Parking3],
  },
  {
    id: "strom",
    title: "Strom",
    description: "Strom ist immer an.",
    longDescription:
      "Der Strom ist standardmäßig eingeschalten. Sollte mal das 3kW-Limit erreicht sein und der Strom ausgehen, kannst du ihn hier wieder einschalten.",
    icon: <Zap className="text-warning" size={24} />,
    photos: [Strom1, Strom2, Strom3Aus, Strom3Ein],
  },
  {
    id: "wasser",
    title: "Wasser",
    description: "Wasser einschalten.",
    longDescription:
      "Schalte bitte zuerst den Strom für den Boiler ein. Danach gehst du zum Parking: Kurz davor findest du auf der rechten Seite den Schalter für das Wasser. Dreh diesen beim B4 gegen den Uhrzeiger.",
    icon: <Droplet className="text-info" size={24} />,
    photos: [Wasser0, Wasser1, WasserAuf, WasserZu],
  },
  {
    id: "gas",
    title: "Gas",
    description: "Gas für den Herd.",
    longDescription:
      "Der Gasschalter befindet sich in der Küche unter der Spüle. Zum Einschalten dreh ihn gegen den Uhrzeiger. Danach kurz überprüfen, ob die Kochplatte funktioniert.",
    icon: <Flame className="text-danger" size={24} />,
    photos: [Gas2Aus, Gas2Zu],
  },
  {
    id: "klima",
    title: "Klima",
    description: "Klima in Betrieb nehmen.",
    longDescription:
      "Hier kannst du den Strom für die Klimaanlagen in den Zimmern einschalten.",
    icon: <Thermometer className="text-warning" size={24} />,
    photos: [Klima1, Klima2],
  },
  {
    id: "beleuchtung",
    title: "Beleuchtung",
    description: "Licht und Dunstabzug.",
    longDescription:
      "Hier kannst du den Strom für Dunstabzugshaube und für die Beleuchtung für die Küche einschalten.",
    icon: <Lightbulb className="text-secondary" size={24} />,
    photos: [KuecheBeleuchtung],
  },
  {
    id: "muell",
    title: "Müll",
    description: "Sardinien trennt streng.",
    longDescription: (
      <>
        <p>
          Installiere die App <strong>Junker</strong>, um bei Unsicherheiten
          schnell zu sehen, was wohin gehört.
        </p>
        <div className="d-flex gap-2 mb-3">
          <a
            href="https://apps.apple.com/us/app/junker-raccolta-differenziata/id982194860"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary"
          >
            iOS App
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=it.giunko.junker"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary"
          >
            Android App
          </a>
        </div>
        <p>
          Mülltrennung wird hier sehr ernst genommen. Bitte verwende für
          Bio-Müll ausschließlich die kompostierbaren Sackerl – diese findest du
          unter der Spüle. Alternativ funktionieren auch die Obstsackerl von
          Eurospin. Andere Müllsäcke findest du hinten im Abstellkasten.
        </p>

        <h6 className="fw-bold mt-3">Mülltrennung kurz erklärt:</h6>
        <ul className="small">
          <li>
            <strong>Bio:</strong> Essensreste und Küchenrolle (da Zellulose).
          </li>
          <li>
            <strong>Karton:</strong> Pizzakartons (sauber), Tetrapacks (ohne
            Verschluss).
          </li>
          <li>
            <strong>Glas & Metall:</strong> Gehören zusammen in einen Behälter.
          </li>
          <li>
            <strong>Restmüll:</strong> Alles, was in keine der oben genannten
            Kategorien passt.
          </li>
        </ul>
        <p className="mt-2">
          Bitte stelle die Müllkisten abends ab 22:00 Uhr auf die Straße. Den
          Hinweis, welcher Müll in welche Tonne kommt, findest du meist auch
          direkt auf der Verpackung.
        </p>
      </>
    ),
    icon: <Trash2 className="text-secondary" size={24} />,
    photos: [JunkerApp],
  },
];

const checkOutItems: ChecklistItem[] = [
  {
    id: "waste_disposal",
    title: "Müll entsorgen",
    description: "Bio- & Restmüll bitte mitnehmen.",
    longDescription:
      "Bitte entsorge den Bio- und Restmüll direkt in Budoni. Auf der Hauptstraße stehen dafür alle 100 bis 150 Meter Mülltonnen bereit. Plastik, Glas und Papiermüll kannst du einfach im Container in der Wohnung lassen.",
    icon: <Trash2 className="text-danger" size={24} />,
  },
  {
    id: "fridge",
    title: "Kühlschrank leeren",
    description: "Kühlschrank komplett leeren.",
    longDescription:
      "Bitte leert den Kühlschrank vollständig und lasst die Kühlschrank- sowie die Gefrierfachtür leicht geöffnet, damit keine Gerüche entstehen.",
    icon: <AlertTriangle className="text-warning" size={24} />,
  },
  {
    id: "fridge_off",
    title: "Kühlschrank abschalten",
    description: "Stromstecker ziehen.",
    longDescription:
      "Bitte öffne die Schublade unter dem Kühlschrank und ziehe den Stecker heraus.",
    icon: <Power className="text-warning" size={24} />,
    photos: [FridgeOff],
  },
  {
    id: "balkonmoebel",
    title: "Balkonmöbel",
    description: "Tisch und Sessel verstauen.",
    longDescription:
      "Klappe den Tisch und die beiden Sessel auf dem Balkon bitte zusammen und stelle sie ins Zimmer.",
    icon: <Home className="text-primary" size={24} />,
  },
  {
    id: "balkon",
    title: "Balkontür",
    description: "Schlafzimmer-Tür abschließen.",
    longDescription:
      "Bitte schließe die Balkontür im Schlafzimmer und lass den Schlüssel einfach stecken.",
    icon: <Home className="text-primary" size={24} />,
  },
  {
    id: "klima_off",
    title: "Klimaanlagen ausschalten",
    description: "Schalter in den Zimmern aus.",
    longDescription:
      "Bitte schalte in den beiden oberen Zimmern die Schalter für die Klimaanlagen aus.",
    icon: <Power className="text-warning" size={24} />,
    photos: [Klima1, Klima2],
  },
  {
    id: "fenster",
    title: "Fenster schließen",
    description: "Jalousien und Fenster schließen.",
    longDescription:
      "Im zweiten Schlafzimmer schließe bitte die Außenjalousien und stelle das Fenster so ein, wie auf den Fotos ersichtlich.",
    icon: <Home className="text-primary" size={24} />,
    photos: [Fenster1, Fenster2],
  },
  {
    id: "gas_water",
    title: "Gas absperren",
    description: "Gas zudrehen & Stecker ziehen.",
    longDescription:
      "Bitte drehe den Gashahn unter der Spüle (rechts in der Küche) im Uhrzeigersinn zu. Ziehe außerdem die beiden Stecker unten links unter der Spüle heraus.",
    icon: <Droplet className="text-info" size={24} />,
    photos: [GasAbdrehen, GeschierspuelerKochfeld],
  },
  {
    id: "beleuchtung_checkout",
    title: "Beleuchtung",
    description: "Licht und Dunstabzug aus.",
    longDescription:
      "Bitte schalte den Schalter für den Dunstabzug und die Küchenbeleuchtung aus.",
    icon: <Lightbulb className="text-secondary" size={24} />,
    photos: [KuecheBeleuchtung],
  },
  {
    id: "alle_schalter",
    title: "Steckerleiste",
    description: "Kaffeemaschine, Toaster & Minibackofen.",
    longDescription:
      "Bitte schalte die Steckerleiste für die Kaffeemaschine, den Toaster und den Minibackofen aus.",
    icon: <Power className="text-secondary" size={24} />,
    photos: [Kaffemaschine],
  },
  {
    id: "sitzpolster",
    title: "Sitzpolster",
    description: "Veranda-Polster verstauen.",
    longDescription:
      "Bitte nimm die Sitzpolster von der Veranda und lege sie in die Wohnung.",
    icon: <Home className="text-primary" size={24} />,
  },
  {
    id: "boiler_wasser",
    title: "Boiler & Wasser",
    description: "Boiler aus & Wasser zudrehen.",
    longDescription:
      "Bitte schalte den Boiler im Badezimmer aus und lass das Fenster gekippt. Nimm dann bitte die Zange vom Check-in, geh zum Parkplatz und schalte dort das Wasser ab.",
    icon: <Droplet className="text-info" size={24} />,
    photos: [Wasser0, Wasser1, WasserZu],
  },
  {
    id: "muellraum",
    title: "Müllraum",
    description: "Müllraum sichern.",
    longDescription:
      "Schließe bitte die Lamellentür zum Müllraum und klemme einen Türstopper zwischen Innentür und Türrahmen. Stelle anschließend den weißen Kindersitz vor die Tür.",
    icon: <Home className="text-primary" size={24} />,
  },
  {
    id: "abschluss",
    title: "Zum Schluss",
    description: "Lichter aus & Türen zu.",
    longDescription:
      "Zum Schluss schalte bitte alle Lichter aus und schließe sowohl die Wohnungstür als auch die Lamellentür.",
    icon: <Power className="text-secondary" size={24} />,
  },
  {
    id: "dankeschoen",
    title: "Dankeschön!",
    description: "Gute Heimreise.",
    longDescription:
      "Vielen Dank, dass du mitgemacht hast! Versprochen: Beim nächsten Mal wird das Ein- und Auschecken viel einfacher. Gute Heimreise!",
    icon: <CheckCircle2 className="text-success" size={24} />,
  },
];

export const Checklist: React.FC<ChecklistProps> = ({ type: propType }) => {
  const [checklistType, setChecklistType] = useState<"checkin" | "checkout">(
    "checkin",
  );
  const [activeItem, setActiveItem] = useState<ChecklistItem | null>(null);

  useEffect(() => {
    if (propType) {
      setChecklistType(propType);
    } else {
      const path = window.location.pathname.toLowerCase();
      if (path.includes("checkout")) {
        setChecklistType("checkout");
      } else {
        setChecklistType("checkin");
      }
    }
  }, [propType]);

  const items = checklistType === "checkin" ? checkInItems : checkOutItems;
  const storageKey = `casa_wadowski_${checklistType}_completed`;

  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(
    () => {
      // 1. Check for URL parameters
      const params = new URLSearchParams(window.location.search);
      const completedParam = params.get("completed");

      if (completedParam) {
        const preCompleted: Record<string, boolean> = {};
        completedParam.split(",").forEach((id) => {
          preCompleted[id.trim()] = true;
        });
        return preCompleted;
      }

      // 2. Fallback to localStorage
      try {
        const saved = localStorage.getItem(storageKey);
        return saved ? JSON.parse(saved) : {};
      } catch {
        return {};
      }
    },
  );

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(checkedItems));
  }, [checkedItems, storageKey]);

  const toggleItem = (id: string, state: boolean) => {
    setCheckedItems((prev) => ({
      ...prev,
      [id]: state,
    }));
  };

  if (activeItem) {
    const isChecked = !!checkedItems[activeItem.id];
    return (
      <div className="bg-light min-vh-100 py-5">
        <div className="container" style={{ maxWidth: "720px" }}>
          <div className="mb-4">
            <button
              onClick={() => setActiveItem(null)}
              className="btn btn-link text-decoration-none text-secondary d-inline-flex align-items-center gap-2 p-0"
            >
              <ArrowLeft size={18} />
              Zurück zur Übersicht
            </button>
          </div>

          <div className="card shadow-sm border-0 rounded-4 p-4">
            <div className="d-flex align-items-center gap-3 mb-3">
              <div className="p-3 bg-light rounded-circle">
                {activeItem.icon}
              </div>
              <h1 className="h3 fw-bold mb-0">{activeItem.title}</h1>
            </div>

            <p className="lead">{activeItem.description}</p>
            <div className="text-secondary">{activeItem.longDescription}</div>

            {/* Photos section */}
            {activeItem.photos && activeItem.photos.length > 0 && (
              <div className="row g-2 my-4">
                {activeItem.photos.map((photo, index) => (
                  <div key={index} className="col-6">
                    <img
                      src={photo}
                      alt={`${activeItem.title} - ${index + 1}`}
                      className="img-fluid rounded-3"
                    />
                  </div>
                ))}
              </div>
            )}

            {activeItem.id !== "dankeschoen" && (
              <div className="d-flex gap-3 mt-4">
                <button
                  onClick={() => {
                    toggleItem(activeItem.id, !isChecked);
                    setActiveItem(null);
                  }}
                  className={`btn ${
                    isChecked ? "btn-outline-success" : "btn-success"
                  } rounded-pill flex-grow-1`}
                >
                  {isChecked
                    ? "Als nicht erledigt markieren"
                    : "Als erledigt markieren"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100 py-5">
      <div className="container" style={{ maxWidth: "720px" }}>
        {/* Header Card */}
        <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
          <div
            className="text-white p-4 text-center position-relative"
            style={{ backgroundColor: "#28a745" }}
          >
            <h1 className="h3 fw-bold mb-1" style={{ color: "#fff" }}>
              Casa Wadowski
            </h1>
            <p className="mb-0 opacity-75" style={{ color: "#fff" }}>
              {checklistType === "checkin"
                ? "Willkommen auf Sardinien!"
                : "Gute Heimreise!"}
            </p>
          </div>
          <div className="card-body p-4 text-center">
            <h2 className="h4 fw-bold mb-2">
              {checklistType === "checkin"
                ? "Check-In Checkliste"
                : "Check-Out Checkliste"}
            </h2>
          </div>
        </div>

        {/* Checklist items */}
        <div className="d-flex flex-column gap-3 mb-4">
          {items.map((item) => {
            const isChecked = !!checkedItems[item.id];
            // Check if it was pre-set via URL
            const params = new URLSearchParams(window.location.search);
            const isHostHandled = params
              .get("completed")
              ?.split(",")
              .includes(item.id);

            return (
              <div
                key={item.id}
                onClick={() => setActiveItem(item)}
                className={`card shadow-sm border-0 rounded-4 p-3 cursor-pointer d-flex flex-row align-items-center gap-3 ${
                  isChecked ? "bg-white opacity-75" : "bg-white"
                }`}
                style={{
                  cursor: "pointer",
                  borderLeft: isChecked
                    ? "5px solid #198754"
                    : "5px solid #6c757d",
                }}
              >
                <div className="flex-shrink-0">
                  {isChecked ? (
                    <CheckCircle2 className="text-success" size={24} />
                  ) : (
                    <Circle className="text-muted" size={24} />
                  )}
                </div>
                <div className="flex-grow-1">
                  <h3
                    className={`h6 fw-bold mb-0 ${isChecked ? "text-decoration-line-through text-muted" : "text-dark"}`}
                  >
                    {item.title}
                  </h3>
                  {isHostHandled && (
                    <span
                      className="badge text-white mt-1"
                      style={{ fontSize: "0.7rem", backgroundColor: "#28a745" }}
                    >
                      Von Casa Wadowski erledigt
                    </span>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <ChevronRight size={20} className="text-secondary" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Checklist;
